import React, { useEffect, useRef, useCallback } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useSpeechToText } from '../hooks/useSpeechToText';

// Speech-to-text-enabled input. Mic button sits inside the field via absolute
// positioning; pulse animation is defined in src/index.css (`animate-dictation-pulse`)
// rather than inline @emotion/react keyframes.

export interface DictationTextFieldProps {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onTranscriptChange?: (text: string) => void;
  label?: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  helperText?: React.ReactNode;
  error?: boolean;
  id?: string;
  className?: string;
}

export default function DictationTextField({
  value = '',
  onChange,
  onTranscriptChange,
  label,
  placeholder,
  multiline = false,
  rows = 3,
  fullWidth = true,
  size,
  helperText,
  error,
  id,
  className,
}: DictationTextFieldProps) {
  const {
    isListening,
    transcript,
    interimTranscript,
    isSupported,
    error: speechError,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechToText();

  const lastTranscriptLengthRef = useRef(0);

  useEffect(() => {
    if (transcript.length > lastTranscriptLengthRef.current) {
      const newText = transcript.slice(lastTranscriptLengthRef.current);
      const currentValue = typeof value === 'string' ? value : '';
      const separator = currentValue.length > 0 && !currentValue.endsWith(' ') ? ' ' : '';
      const updatedValue = currentValue + separator + newText;

      onTranscriptChange?.(updatedValue);

      if (onChange) {
        const syntheticEvent = {
          target: { value: updatedValue },
          currentTarget: { value: updatedValue },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    }
    lastTranscriptLengthRef.current = transcript.length;
  }, [transcript, value, onChange, onTranscriptChange]);

  useEffect(() => {
    if (value === '' || value === undefined) {
      lastTranscriptLengthRef.current = 0;
      resetTranscript();
    }
  }, [value, resetTranscript]);

  const handleMicClick = useCallback(() => {
    if (isListening) stopListening();
    else startListening();
  }, [isListening, startListening, stopListening]);

  const displayValue = isListening && interimTranscript
    ? `${value}${value && !String(value).endsWith(' ') ? ' ' : ''}${interimTranscript}`
    : value;

  const tooltipLabel = !isSupported
    ? 'Speech recognition is not supported in this browser'
    : isListening
      ? 'Stop dictation'
      : 'Start dictation';

  const micButton = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={handleMicClick}
      disabled={!isSupported}
      aria-label={isListening ? 'Stop dictation' : 'Start dictation'}
      className={cn(
        'absolute right-1 h-7 w-7',
        multiline ? 'bottom-1' : 'top-1/2 -translate-y-1/2',
        isListening ? 'text-primary animate-dictation-pulse' : 'text-muted-foreground',
      )}
    >
      {isListening ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
    </Button>
  );

  const inputClass = cn(
    'pr-10',
    size === 'small' && !multiline && 'h-8 text-xs',
    error && 'border-destructive focus-visible:ring-destructive',
  );

  return (
    <div className={cn(fullWidth ? 'w-full' : '', 'flex flex-col gap-1.5', className)}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="relative">
        {multiline ? (
          <Textarea
            id={id}
            value={displayValue}
            onChange={onChange}
            placeholder={placeholder}
            rows={rows}
            className={inputClass}
          />
        ) : (
          <Input
            id={id}
            value={displayValue}
            onChange={onChange}
            placeholder={placeholder}
            className={inputClass}
          />
        )}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>{micButton}</span>
            </TooltipTrigger>
            <TooltipContent>{tooltipLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {(speechError || helperText) && (
        <p className={cn('text-xs', (error || speechError) ? 'text-destructive' : 'text-muted-foreground')}>
          {speechError || helperText}
        </p>
      )}
    </div>
  );
}
